export const handleSwipeDetails = async ({
  setSwipeModal,
  swipemodal,
  workhoursDetails,
  triggerSwipeDetails,
  loginId,
  dayjs,
  setCalculatedProductiveProgress,
  setCalculatedOfficeProgress,
  setTimeRemaining,
  setExpectedHoursFormatted,
  setOfficeHoursRemaining,
}) => {
  setSwipeModal(!swipemodal);

  try {
    const response = await workhoursDetails({
      empID: loginId,
      dateObj: dayjs().format("YYYY-MM-DD"),
    }).unwrap();
    const fullDayTime = response?.data?.fulldayTime || "00:00";
    const shiftInTime = response?.data?.inTime || "00:00";
    const shiftDate = dayjs().format("YYYY-MM-DD");

    const swipeResponse = await triggerSwipeDetails({
      empID: loginId,
    }).unwrap();
    if (!swipeResponse?.data || !Array.isArray(swipeResponse.data)) return;

    const now = new Date();

    const parseTime = (dateStr, timeStr) =>
      new Date(`${dateStr}T${timeStr}:00`);

    const formatHMS = (seconds) => {
      const s = Math.floor(Math.max(0, seconds));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      return `${h.toString().padStart(2, "0")}h ${m.toString().padStart(2, "0")}m ${sec.toString().padStart(2, "0")}s`;
    };

    const fullDayParts = fullDayTime.split(":").map(Number);
    const expectedBreakSeconds = 3600;
    const expectedWorkSeconds =
      fullDayParts[0] * 3600 + fullDayParts[1] * 60 - expectedBreakSeconds;
    const totalShiftSeconds = fullDayParts[0] * 3600 + fullDayParts[1] * 60;

    let sortedSwipes = [...swipeResponse.data].sort(
      (a, b) =>
        new Date(a.LogDateTime).getTime() - new Date(b.LogDateTime).getTime(),
    );

    // Filter duplicate consecutive swipes: keep latest "in", keep first "out"
    const filterSwipes = (swipes) => {
      const result = [];
      for (let i = 0; i < swipes.length; i++) {
        const cur = swipes[i];
        const prev = result[result.length - 1];
        if (prev && prev.Direction === cur.Direction) {
          if (cur.Direction === "in") result[result.length - 1] = cur;
        } else {
          result.push(cur);
        }
      }
      return result;
    };

    sortedSwipes = filterSwipes(sortedSwipes);

    if (sortedSwipes.length === 0) {
      setCalculatedProductiveProgress(0);
      setCalculatedOfficeProgress(0);
      setTimeRemaining("00h 00m 00s");
      setExpectedHoursFormatted(formatHMS(expectedWorkSeconds));
      setOfficeHoursRemaining(
        `Remaining: ${formatHMS(totalShiftSeconds)} / Expected out Time: 00:00`,
      );
      return;
    }

    const firstInSwipe = sortedSwipes.find((s) => s.Direction === "in");
    if (!firstInSwipe) return;

    const firstInTime = new Date(firstInSwipe.LogDateTime);
    const shiftIn = parseTime(shiftDate, shiftInTime);
    const lastSwipe = sortedSwipes[sortedSwipes.length - 1];

    // Late by calculation
    const lateBySeconds = Math.max(
      (firstInTime.getTime() - shiftIn.getTime()) / 1000,
      0,
    );
    const shiftOutTime = new Date(shiftIn.getTime() + totalShiftSeconds * 1000);
    const expectedOutTimeDate = new Date(
      shiftOutTime.getTime() + lateBySeconds * 1000,
    );

    // Early time (swipes before shift start)
    let earlyTimeMs = 0;
    let lastInBeforeShift = null;
    for (const swipe of sortedSwipes) {
      const t = new Date(swipe.LogDateTime);
      if (swipe.Direction === "in") {
        lastInBeforeShift = t;
      } else if (swipe.Direction === "out" && lastInBeforeShift) {
        if (lastInBeforeShift < shiftIn) {
          const end = t < shiftIn ? t : shiftIn;
          earlyTimeMs += end.getTime() - lastInBeforeShift.getTime();
        }
        lastInBeforeShift = null;
      }
    }

    // Total in/out seconds
    let totalInSeconds = 0;
    let totalOutSeconds = 0;
    let lastIn = null;
    let lastOut = null;

    for (const swipe of sortedSwipes) {
      const t = new Date(swipe.LogDateTime);
      if (swipe.Direction === "in") {
        if (lastOut) {
          totalOutSeconds += (t.getTime() - lastOut.getTime()) / 1000;
          lastOut = null;
        }
        lastIn = t;
      } else if (swipe.Direction === "out") {
        if (lastIn) {
          totalInSeconds += (t.getTime() - lastIn.getTime()) / 1000;
          lastIn = null;
        }
        lastOut = t;
      }
    }
    if (lastIn) totalInSeconds += (now.getTime() - lastIn.getTime()) / 1000;

    // Extra break beyond expected 1hr
    const extraBreakSeconds =
      totalOutSeconds > expectedBreakSeconds
        ? totalOutSeconds - expectedBreakSeconds
        : 0;

    // Adjusted out time
    let adjustedOutTimeDate = new Date(
      expectedOutTimeDate.getTime() + extraBreakSeconds * 1000 - earlyTimeMs,
    );
    if (adjustedOutTimeDate < expectedOutTimeDate)
      adjustedOutTimeDate = expectedOutTimeDate;

    const lastOutTimeObj = new Date(lastSwipe.LogDateTime);
    const effectiveNow =
      lastSwipe.Direction === "in" ? now.getTime() : lastOutTimeObj.getTime();

    // Office hour progress: time elapsed from firstIn vs expected shift duration
    const officeHourProgress = Math.min(
      Math.floor(
        ((effectiveNow - firstInTime.getTime()) /
          (expectedOutTimeDate.getTime() - firstInTime.getTime())) *
          100,
      ),
      100,
    );

    // Productive hour progress: actual in-time vs expected work seconds
    const productiveHourProgress = Math.min(
      Math.floor((totalInSeconds / expectedWorkSeconds) * 100),
      100,
    );

    // Remaining work
    const remainingWorkSeconds =
      lastSwipe.Direction === "out"
        ? Math.max(
            (expectedOutTimeDate.getTime() - lastOutTimeObj.getTime()) / 1000,
            0,
          )
        : Math.max((expectedOutTimeDate.getTime() - now.getTime()) / 1000, 0);

    // Remaining extra break
    let remainingExtraBreakSeconds = 0;
    if (extraBreakSeconds > 0) {
      if (
        lastSwipe.Direction === "out" &&
        adjustedOutTimeDate > lastOutTimeObj
      ) {
        remainingExtraBreakSeconds = Math.max(
          (adjustedOutTimeDate.getTime() -
            Math.max(expectedOutTimeDate.getTime(), lastOutTimeObj.getTime())) /
            1000,
          0,
        );
      } else if (lastSwipe.Direction === "in") {
        if (now < expectedOutTimeDate) {
          remainingExtraBreakSeconds = extraBreakSeconds;
        } else if (now <= adjustedOutTimeDate) {
          remainingExtraBreakSeconds = Math.max(
            (adjustedOutTimeDate.getTime() - now.getTime()) / 1000,
            0,
          );
        }
      }
    }

    const totalRemaining = remainingWorkSeconds + remainingExtraBreakSeconds;
    const overTimeSeconds = totalInSeconds - expectedWorkSeconds;
    const overTime = overTimeSeconds > 0 ? formatHMS(overTimeSeconds) : null;

    const isCompleted = now >= adjustedOutTimeDate && overTime !== null;
    const officeHoursRemainingText = isCompleted
      ? `🎉 Completed! + ${formatHMS(overTimeSeconds)} OverTime`
      : `Need ${formatHMS(remainingWorkSeconds)}${remainingExtraBreakSeconds > 0 ? ` + ${formatHMS(remainingExtraBreakSeconds)} (extra break)` : " + 00h 00m 00s"} to complete`;

    const displayOutTime = new Date(expectedOutTimeDate.getTime() + 60 * 1000);
    const expectedOutFormatted = displayOutTime.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });

    setCalculatedProductiveProgress(productiveHourProgress);
    setCalculatedOfficeProgress(officeHourProgress);
    setTimeRemaining(formatHMS(totalInSeconds));
    setExpectedHoursFormatted(formatHMS(expectedWorkSeconds));
    setOfficeHoursRemaining(
      totalRemaining <= 0
        ? `Completed! / Expected out Time: ${expectedOutFormatted}`
        : `Remaining: ${formatHMS(totalRemaining)} / Expected out Time: ${expectedOutFormatted}`,
    );
  } catch (error) {
    // Handle error silently
  }
};
